import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Receipt } from "lucide-react";
import { getById, create, update } from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_PRICELIST } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "pricelist";
const EMPTY_ITEM = { name: "", price: "", desc: "" };

function PricelistTab({ type, data, onChange, onSave, saving }) {
  const [localData, setLocalData] = useState(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const updateField = (field, value) => {
    const next = { ...localData, [field]: value };
    setLocalData(next);
    onChange(next);
  };

  const updateItem = (index, field, value) => {
    const items = [...(localData.items || [])];
    items[index] = { ...items[index], [field]: value };
    setLocalData({ ...localData, items });
  };

  const addItem = () => {
    setLocalData({
      ...localData,
      items: [...(localData.items || []), { ...EMPTY_ITEM }],
    });
  };

  const removeItem = (index) => {
    const items = (localData.items || []).filter((_, i) => i !== index);
    setLocalData({ ...localData, items });
  };

  const addFreebie = () => {
    setLocalData({
      ...localData,
      freebies: [...(localData.freebies || []), ""],
    });
  };

  const updateFreebie = (index, value) => {
    const freebies = [...(localData.freebies || [])];
    freebies[index] = value;
    setLocalData({ ...localData, freebies });
  };

  const removeFreebie = (index) => {
    const freebies = (localData.freebies || []).filter((_, i) => i !== index);
    setLocalData({ ...localData, freebies });
  };

  return (
    <div className="space-y-6">
      {/* Label & Subtitle */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-[#a6a196]">Label</label>
          <Input
            value={localData?.label || ""}
            onChange={(e) => updateField("label", e.target.value)}
            placeholder="Reguler"
            className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm text-[#a6a196]">Subtitle</label>
          <Input
            value={localData?.subtitle || ""}
            onChange={(e) => updateField("subtitle", e.target.value)}
            placeholder="Pilihan ringkas untuk momen istimewa"
            className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
          />
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm text-[#a6a196] font-medium">Items</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            className="border-[#d4af37]/30 text-[#d4af37]"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Item
          </Button>
        </div>

        {(localData?.items || []).length === 0 ? (
          <p className="text-sm text-[#a6a196]/50 italic py-4 text-center">
            Belum ada item
          </p>
        ) : (
          <div className="space-y-3">
            {localData.items.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-3 rounded-lg border border-[#d4af37]/10 bg-[#0a0908]"
              >
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    value={item.name}
                    onChange={(e) => updateItem(i, "name", e.target.value)}
                    placeholder="Nama item"
                    className="bg-[#15120f] border-[#d4af37]/15 text-[#fdfbf7] placeholder:text-[#a6a196]/50 text-sm"
                  />
                  <Input
                    value={item.price}
                    onChange={(e) => updateItem(i, "price", e.target.value)}
                    placeholder="150K"
                    className="bg-[#15120f] border-[#d4af37]/15 text-[#fdfbf7] placeholder:text-[#a6a196]/50 text-sm"
                  />
                  <Input
                    value={item.desc}
                    onChange={(e) => updateItem(i, "desc", e.target.value)}
                    placeholder="Deskripsi singkat"
                    className="bg-[#15120f] border-[#d4af37]/15 text-[#fdfbf7] placeholder:text-[#a6a196]/50 text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeItem(i)}
                  className="h-8 w-8 shrink-0 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10 mt-0.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Premium-only: Freebies + Note */}
      {type === "premium" && (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-[#a6a196] font-medium">
                Freebies
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addFreebie}
                className="border-[#d4af37]/30 text-[#d4af37]"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Freebie
              </Button>
            </div>

            <p className="text-[10px] text-[#a6a196] italic mt-1">Bonus gratis yang termasuk dalam paket</p>

            {(localData?.freebies || []).length === 0 ? (
              <p className="text-sm text-[#a6a196]/50 italic py-2">
                Belum ada bonus
              </p>
            ) : (
              <div className="space-y-2">
                {localData.freebies.map((fb, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={fb}
                      onChange={(e) => updateFreebie(i, e.target.value)}
                      placeholder="Bulu Mata"
                      className="bg-[#0a0908] border-[#d4af37]/15 text-[#fdfbf7] placeholder:text-[#a6a196]/50 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFreebie(i)}
                      className="h-8 w-8 shrink-0 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm text-[#a6a196]">Note</label>
            <Textarea
              value={localData?.note || ""}
              onChange={(e) => updateField("note", e.target.value)}
              placeholder="Free Softlens hanya untuk Premium Makeup"
              className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50 min-h-[60px]"
            />
          </div>
        </>
      )}

      {/* Save button */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={() => onSave(type, localData)}
          disabled={saving}
          className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Menyimpan...
            </>
          ) : (
            `Simpan ${type === "reguler" ? "Reguler" : "Premium"}`
          )}
        </Button>
      </div>
    </div>
  );
}

export default function PricelistEditor() {
  const [reguler, setReguler] = useState(null);
  const [premium, setPremium] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataEmpty, setDataEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("reguler");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, prem] = await Promise.all([
        getById(COLLECTION, "reguler"),
        getById(COLLECTION, "premium"),
      ]);
      const bothEmpty = reg === null && prem === null;
      setDataEmpty(bothEmpty);
      setReguler(reg || SEED_PRICELIST.reguler);
      setPremium(prem || SEED_PRICELIST.premium);
    } catch {
      toast.error("Gagal memuat daftar harga");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (type, data) => {
    setSaving(true);
    try {
      const id = type;
      const existing = type === "reguler" ? reguler : premium;
      if (existing?.id) {
        await update(COLLECTION, id, data);
      } else {
        await create(COLLECTION, data, id);
      }
      toast.success(`${type === "reguler" ? "Reguler" : "Premium"} berhasil disimpan!`);
      await updateCollectionVersion(COLLECTION);
      loadData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
            <h1 className="text-2xl font-display text-[#fdfbf7]">
              Price List
            </h1>
          </div>
          <p className="text-sm text-[#a6a196] ml-4">
            Kelola daftar harga reguler & premium
          </p>
        </div>
        <SeedDataButton
          collectionName={COLLECTION}
          data={[
            { id: "reguler", ...SEED_PRICELIST.reguler },
            { id: "premium", ...SEED_PRICELIST.premium },
          ]}
          onComplete={loadData}
          isEmpty={dataEmpty}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="bg-[#0a0908] border border-[#d4af37]/10">
            <TabsTrigger
              value="reguler"
              className="data-[state=active]:bg-[#d4af37]/10 data-[state=active]:text-[#d4af37] text-[#a6a196]"
            >
              Reguler
            </TabsTrigger>
            <TabsTrigger
              value="premium"
              className="data-[state=active]:bg-[#d4af37]/10 data-[state=active]:text-[#d4af37] text-[#a6a196]"
            >
              Premium
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reguler" className="mt-6">
            <div className="rounded-lg border border-[#d4af37]/10 bg-[#0a0908] p-6">
              <PricelistTab
                type="reguler"
                data={reguler}
                onChange={setReguler}
                onSave={handleSave}
                saving={saving}
              />
            </div>
          </TabsContent>

          <TabsContent value="premium" className="mt-6">
            <div className="rounded-lg border border-[#d4af37]/10 bg-[#0a0908] p-6">
              <PricelistTab
                type="premium"
                data={premium}
                onChange={setPremium}
                onSave={handleSave}
                saving={saving}
              />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
